import { find, isEmpty } from "lodash";

import COUNTRIES from "../constants/countries";

/** Load an image URL and resolve its natural pixel dimensions. */
export const loadImageDimensions = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new window.Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

export const EXPORT_DPI = 300;

/** inches → meters for PNG pHYs (pixels per meter). */
const INCHES_PER_METER = 39.3701;

/** CRC32 table for PNG chunk checksums. */
const PNG_CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function pngCrc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = PNG_CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function dataUrlToUint8Array(dataUrl) {
  const comma = String(dataUrl || "").indexOf(",");
  if (comma < 0) return null;
  const binary = atob(dataUrl.slice(comma + 1));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Insert or replace a PNG pHYs chunk so Photoshop / Preview report the
 * intended print DPI. Canvas toDataURL omits density metadata (defaults to 72).
 */
export function embedPngDpi(pngBytes, dpi = EXPORT_DPI) {
  const src =
    pngBytes instanceof Uint8Array
      ? pngBytes
      : new Uint8Array(pngBytes || []);
  if (src.length < 24) return src;
  // PNG signature
  if (
    src[0] !== 0x89 ||
    src[1] !== 0x50 ||
    src[2] !== 0x4e ||
    src[3] !== 0x47
  ) {
    return src;
  }

  const ppm = Math.round(Number(dpi) * INCHES_PER_METER);
  if (!ppm) return src;

  const physData = new Uint8Array(9);
  const view = new DataView(physData.buffer);
  view.setUint32(0, ppm);
  view.setUint32(4, ppm);
  physData[8] = 1; // unit: meter

  const typeAndData = new Uint8Array(4 + physData.length);
  typeAndData.set([0x70, 0x48, 0x59, 0x73]); // pHYs
  typeAndData.set(physData, 4);
  const crc = pngCrc32(typeAndData);

  const chunk = new Uint8Array(4 + typeAndData.length + 4);
  const chunkView = new DataView(chunk.buffer);
  chunkView.setUint32(0, physData.length);
  chunk.set(typeAndData, 4);
  chunkView.setUint32(4 + typeAndData.length, crc);

  // Rebuild: signature + IHDR + pHYs + remaining (skip any existing pHYs).
  const SIG = 8;
  let i = SIG;
  let ihdrEnd = -1;
  const parts = [src.subarray(0, SIG)];

  while (i + 8 <= src.length) {
    const len = new DataView(src.buffer, src.byteOffset + i, 4).getUint32(0);
    const type = String.fromCharCode(
      src[i + 4],
      src[i + 5],
      src[i + 6],
      src[i + 7]
    );
    const chunkEnd = i + 12 + len;
    if (chunkEnd > src.length) break;

    if (type === "IHDR") {
      parts.push(src.subarray(i, chunkEnd));
      ihdrEnd = chunkEnd;
      parts.push(chunk);
    } else if (type !== "pHYs") {
      parts.push(src.subarray(i, chunkEnd));
    }
    // skip existing pHYs — replaced above after IHDR

    i = chunkEnd;
    if (type === "IEND") break;
  }

  if (ihdrEnd < 0) return src;

  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/** Export a canvas PNG data URL with embedded print DPI metadata. */
export function canvasToPngDataUrlWithDpi(canvas, dpi = EXPORT_DPI) {
  const raw = canvas.toDataURL("image/png");
  try {
    const bytes = dataUrlToUint8Array(raw);
    if (!bytes) return raw;
    const withDpi = embedPngDpi(bytes, dpi);
    return `data:image/png;base64,${bytesToBase64(withDpi)}`;
  } catch (err) {
    // Large production PNGs can OOM during base64 round-trip; still return
    // a usable PNG so trimmed previews/downloads are not blank.
    console.warn("[canvasToPngDataUrlWithDpi]", err);
    return raw;
  }
}

/**
 * Crop transparent padding from a production PNG (alpha bbox), matching
 * BatchImagesProcess2's removeTransparencies behavior. Returns a data URL
 * plus trimmed pixel size at EXPORT_DPI for print-size display.
 */
export async function trimTransparentProductionFile(url) {
  if (!url || typeof url !== "string") return null;

  let objectUrl = null;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const blob = await res.blob();
    objectUrl = URL.createObjectURL(blob);

    const img = await new Promise((resolve, reject) => {
      const el = new window.Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image load failed"));
      el.src = objectUrl;
    });

    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) return null;

    const canvas = document.createElement("canvas");
    canvas.width = srcW;
    canvas.height = srcH;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);

    const { data } = ctx.getImageData(0, 0, srcW, srcH);
    let minX = srcW;
    let minY = srcH;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < srcH; y += 1) {
      for (let x = 0; x < srcW; x += 1) {
        const alpha = data[(y * srcW + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    // Fully transparent — keep the original frame.
    if (maxX < 0) {
      return {
        dataUrl: canvasToPngDataUrlWithDpi(canvas, EXPORT_DPI),
        w: srcW,
        h: srcH,
        originalW: srcW,
        originalH: srcH,
      };
    }

    const trimW = maxX - minX + 1;
    const trimH = maxY - minY + 1;
    const trimmed = document.createElement("canvas");
    trimmed.width = trimW;
    trimmed.height = trimH;
    trimmed
      .getContext("2d")
      .drawImage(canvas, minX, minY, trimW, trimH, 0, 0, trimW, trimH);

    return {
      dataUrl: canvasToPngDataUrlWithDpi(trimmed, EXPORT_DPI),
      w: trimW,
      h: trimH,
      originalW: srcW,
      originalH: srcH,
    };
  } catch (err) {
    console.warn("[trimTransparentProductionFile]", err);
    return null;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/** Human-readable print size from canvas pixels at export DPI. */
export const formatPrintSizeInches = (widthPx, heightPx, dpi = EXPORT_DPI) => {
  if (!widthPx || !heightPx || !dpi) return null;
  const w = (widthPx / dpi).toFixed(1);
  const h = (heightPx / dpi).toFixed(1);
  return `${w}" × ${h}"`;
};

/**
 * Download filename like: Richardson Low Pro Trucker 115 4.0" × 2.0" Front
 * Strips path separators so browsers accept the download attribute.
 */
export const buildPrintFileDownloadName = ({
  productName,
  printSize,
  placement,
}) => {
  const parts = [productName, printSize, placement]
    .map((p) => String(p || "").trim())
    .filter(Boolean);
  // Keep inch marks (") and ×; strip only path/OS-illegal characters.
  const name = parts.join(" ").replace(/[/\\?%*:|<>]/g, "-").trim();
  return name || "print-file";
};

/**
 * Middle-truncate long filenames for dense tables.
 * If length > 26: first 13 of base + "…" + last 6 of base + extension.
 */
export const truncFile = (f) => {
  const name = String(f || "");
  if (name.length <= 26) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base.slice(0, 13)}…${base.slice(-6)}${ext}`;
};

/** Infer a file extension from a URL or content-type-ish name. */
export const guessImageExtension = (url, fallback = "png") => {
  const match = String(url || "").match(/\.([a-zA-Z0-9]{3,4})(?:\?|#|$)/);
  if (match) return match[1].toLowerCase();
  return fallback;
};

const MACHINE_FILE_EXTS = new Set([
  "dst",
  "pes",
  "jef",
  "exp",
  "vp3",
  "xxx",
]);

/** Build a download basename without double extensions (e.g. logo.png.dst). */
export const downloadBaseName = (imageName, ext) => {
  let base = String(imageName || "download").trim() || "download";
  const extNorm = String(ext || "").replace(/^\./, "").toLowerCase();
  if (extNorm) {
    base = base.replace(new RegExp(`\\.${extNorm}$`, "i"), "");
  }
  // Machine files often inherit the artwork name (foo.png) — strip that.
  if (MACHINE_FILE_EXTS.has(extNorm)) {
    base = base.replace(/\.(png|jpe?g|webp|gif|svg|bmp)$/i, "");
  }
  return base || "download";
};

/**
 * Download an image with an optional production-style name.
 * If `printSizeUrl` is provided and `printSize` is not, loads that image to
 * derive print size at EXPORT_DPI (for uploaded/source files tied to a print).
 */
export const downloadNamedPrintImage = async (
  e,
  {
    url,
    productName,
    placement,
    printSize,
    printSizeUrl,
    name,
  } = {}
) => {
  e?.preventDefault?.();
  if (!url) return;

  let size = printSize || null;
  if (!size && printSizeUrl) {
    const dims = await loadImageDimensions(printSizeUrl);
    if (dims) size = formatPrintSizeInches(dims.w, dims.h, EXPORT_DPI);
  }

  const downloadName =
    name ||
    buildPrintFileDownloadName({
      productName,
      printSize: size,
      placement,
    });

  return downloadImage(e || { preventDefault() {} }, {
    url,
    name: downloadName,
  });
};

const triggerBlobDownload = (buffer, baseName, ext, contentType) => {
  const objectUrl = window.URL.createObjectURL(
    new Blob([buffer], contentType ? { type: contentType } : undefined)
  );
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", `${baseName}.${ext}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const dataUrlToArrayBuffer = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.arrayBuffer();
};

/** Encode path segments so filenames with ( ) & spaces fetch reliably. */
export const encodeAssetUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  try {
    const parsed = new URL(url);
    parsed.pathname = parsed.pathname
      .split("/")
      .map((seg) => {
        if (!seg) return seg;
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
    return parsed.toString();
  } catch {
    return url;
  }
};

const fetchImageBuffer = async (url) => {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }
  return {
    buffer: await response.arrayBuffer(),
    contentType: response.headers.get("content-type"),
  };
};

/**
 * Download an image. Supports http(s) URLs and data: URLs (client-trimmed files).
 * Tries a direct CORS fetch first (GCS public objects allow it). The server
 * proxy is only a fallback — it base64-encodes the whole file and fails on
 * large originals (~20MB upscales).
 */
export const downloadImage = async (e, image) => {
  e?.preventDefault?.();

  const { name: imageName, url: rawUrl } = image || {};

  // Client-trimmed production files pass data URLs (not http).
  if (typeof rawUrl === "string" && rawUrl.startsWith("data:")) {
    const mime = rawUrl.match(/^data:([^;]+);/)?.[1] || "image/png";
    const ext =
      mime.includes("jpeg") || mime.includes("jpg")
        ? "jpg"
        : mime.includes("webp")
          ? "webp"
          : "png";
    const baseName = String(imageName || "download").replace(
      new RegExp(`\\.${ext}$`, "i"),
      ""
    );
    try {
      const buffer = await dataUrlToArrayBuffer(rawUrl);
      triggerBlobDownload(buffer, baseName, ext, mime);
    } catch (err) {
      console.log(err);
    }
    return;
  }

  const resolved = resolveExternalImageUrl(rawUrl) || extractHttpUrl(rawUrl);
  const url = resolved ? encodeAssetUrl(resolved) : null;
  if (!url) return;

  const ext = guessImageExtension(url, "png");
  const baseName = downloadBaseName(imageName, ext);
  const isMachineFile = MACHINE_FILE_EXTS.has(String(ext).toLowerCase());

  // 1) Direct CORS fetch. Public GCS objects send Access-Control-Allow-Origin: *.
  try {
    const { buffer, contentType } = await fetchImageBuffer(url);
    triggerBlobDownload(buffer, baseName, ext, contentType);
    return;
  } catch (err) {
    console.log(err);
  }

  // 2) Server proxy for hosts that actually block CORS (Dropbox, some Firebase).
  if (!isMachineFile && isCorsRestrictedImageHost(url)) {
    try {
      const { proxyExternalImage } = await import("../api/batches");
      const response = await proxyExternalImage(url);
      const dataUrl = response?.data?.dataUrl;
      if (dataUrl) {
        const buffer = await dataUrlToArrayBuffer(dataUrl);
        const mime = String(dataUrl).match(/^data:([^;]+);/)?.[1] || null;
        triggerBlobDownload(buffer, baseName, ext, mime);
        return;
      }
    } catch (err) {
      console.log(err);
    }
  }

  // 3) Last resort: open the file URL.
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${baseName}.${ext}`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (_) {
    // ignore
  }
};

export const generateKey = (pre = "") => {
  return `${pre}_${new Date().getTime()}`;
};

export const generateID = () => {
  let array = new Uint32Array(8);
  window.crypto.getRandomValues(array);
  let str = "";
  for (let i = 0; i < array.length; i++) {
    str += (i < 2 || i > 5 ? "" : "-") + array[i].toString(16).slice(-4);
  }
  return str;
};

/** Reject missing ids and the literal "undefined"/"null" strings from bad links. */
export const isValidOrderId = (id) => {
  if (id == null) return false;
  const s = String(id).trim();
  return Boolean(s) && s !== "undefined" && s !== "null";
};

export const getCountryFromId = (id) => {
  return find(COUNTRIES, (c) => c.country_id == id);
};

export const testDomains = [
  "localhost",
  "riverr-enterprise-user.web.app",
  "riverr-enterprise-test.web.app",
  "spenterprise1.myriverr.com",
  "riverr-enterprise-user--new-ui-payments-qwd80gch.web.app",
];

export const isTestEnv = testDomains.includes(window.location.hostname);

const DRIVE_ID_PATTERNS = [
  /drive\.usercontent\.google\.com\/download\?(?:[^&]*&)*id=([\w-]+)/,
  /drive\.google\.com\/uc\?(?:[^&]*&)*id=([\w-]+)/,
  /drive\.google\.com\/file\/d\/([\w-]+)/,
  /drive\.google\.com\/open\?(?:[^&]*&)*id=([\w-]+)/,
  /drive\.google\.com\/thumbnail\?(?:[^&]*&)*id=([\w-]+)/,
];

const toRenderableGoogleImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;

  for (const pattern of DRIVE_ID_PATTERNS) {
    const match = url.match(pattern);
    if (match) {
      return `https://lh3.googleusercontent.com/d/${match[1]}=w1600`;
    }
  }

  return url;
};

const isLocalDevHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/**
 * S&S Activewear serves `_fs` (small) / `_fm` (medium) / `_fl` (large).
 * Inventory thumbs often store medium paths; bump to large for crisp UI.
 * Color swatches have no large variant — leave those alone.
 */
export const upgradeSsActivewearImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (!/ssactivewear\.com/i.test(url)) return url;
  if (/\/ColorSwatch\//i.test(url)) return url;
  return url
    .replace(/_fs(\.[a-zA-Z0-9]+)(\?|#|$)/, "_fl$1$2")
    .replace(/_fm(\.[a-zA-Z0-9]+)(\?|#|$)/, "_fl$1$2");
};

/**
 * Shopify CDN often stores `_small` / `_100x100` / `?width=100` variants.
 * Strip size suffixes and request a larger render for inventory thumbs.
 */
export const upgradeShopifyImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (!/cdn\.shopify\.com|shopifycdn\.com|\.myshopify\.com/i.test(url)) {
    return url;
  }
  let out = url.replace(
    /_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*)(?=\.(?:jpe?g|png|webp|gif))/i,
    "",
  );
  try {
    const parsed = new URL(out);
    const width = parseInt(parsed.searchParams.get("width") || "0", 10);
    if (!Number.isFinite(width) || width < 400) {
      parsed.searchParams.set("width", "800");
    }
    parsed.searchParams.delete("height");
    out = parsed.toString();
  } catch {
    // Keep path-stripped URL if query parsing fails.
  }
  return out;
};

/** Bump tiny Google user-content size tokens (=s64 / =w100) for retina thumbs. */
export const upgradeGoogleUserContentImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (!/googleusercontent\.com/i.test(url)) return url;
  return url
    .replace(/=s\d+(-[a-z]+)?/gi, "=w800")
    .replace(/=w\d+(-[a-z]+)?/gi, "=w800");
};

/**
 * Best-effort inventory thumb URL: upgrade known CDN size tokens, then
 * normalize Drive / S&S / Shopify / Google user-content links for display.
 */
export const resolveInventoryThumbUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const upgraded = upgradeGoogleUserContentImageUrl(
    upgradeShopifyImageUrl(upgradeSsActivewearImageUrl(url.trim())),
  );
  return resolveSupplierImageUrl(upgraded);
};

/**
 * Convert Google Drive share/download links into embeddable lh3 CDN URLs.
 * On localhost, route lh3 images through the dev-server proxy (see setupProxy.js)
 * because Google returns HTTP 429 for requests carrying a localhost Referer.
 * Also upgrades S&S medium/small paths to large for sharper inventory thumbs.
 */
export const resolveSupplierImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;

  const upgraded = upgradeSsActivewearImageUrl(url);
  const renderable = toRenderableGoogleImageUrl(upgraded);

  if (
    isLocalDevHost() &&
    renderable.startsWith("https://lh3.googleusercontent.com/")
  ) {
    return renderable.replace(
      "https://lh3.googleusercontent.com/",
      "/supplier-images/",
    );
  }

  return renderable;
};

export const isHttpUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

/** Millisecond timestamp embedded in generated storage URLs / filenames. */
export const extractAssetTimestamp = (value) => {
  const match = String(value || "").match(/(\d{13})/);
  return match ? Number(match[1]) : 0;
};

const assetEntryUrl = (entry) =>
  extractHttpUrl(
    entry?.url,
    entry?.previewImg,
    entry?.storageInfo?.publicUrl,
    entry?.printFile?.url
  );

/**
 * Merge a color-keyed canvas/preview map with `default`, per placement.
 * When both the color and default have a file, keep the newer URL.
 */
export const resolveColorKeyedPlacements = (colorKeyedMap, color) => {
  if (!colorKeyedMap || typeof colorKeyedMap !== "object") return {};
  let colorKey = null;
  if (color != null && colorKeyedMap[color]) {
    colorKey = color;
  } else if (color != null) {
    const needle = String(color).toLowerCase();
    colorKey = Object.keys(colorKeyedMap).find(
      (k) => String(k).toLowerCase() === needle
    );
  }
  const colorMap = (colorKey && colorKeyedMap[colorKey]) || {};
  const defaultMap = colorKeyedMap.default || {};
  const out = {};
  const placementIds = new Set([
    ...Object.keys(colorMap),
    ...Object.keys(defaultMap),
  ]);
  placementIds.forEach((plc) => {
    const colorEntry = colorMap[plc];
    const defaultEntry = defaultMap[plc];
    const colorUrl = assetEntryUrl(colorEntry);
    const defaultUrl = assetEntryUrl(defaultEntry);
    if (colorUrl && defaultUrl) {
      out[plc] =
        extractAssetTimestamp(defaultUrl) > extractAssetTimestamp(colorUrl)
          ? defaultEntry
          : colorEntry;
    } else if (colorUrl) {
      out[plc] = colorEntry;
    } else if (defaultUrl) {
      out[plc] = defaultEntry;
    }
  });
  return out;
};

export const extractHttpUrl = (...candidates) => {
  for (const candidate of candidates) {
    if (isHttpUrl(candidate)) return candidate.trim();
  }
  return null;
};

/** Convert Dropbox sharing links to direct-download URLs (dl=1). */
export const convertDropboxUrl = (url) => {
  if (!url || typeof url !== "string" || !url.includes("dropbox.com/")) {
    return url;
  }
  if (url.includes("dropboxusercontent.com")) return url;
  if (url.includes("dl=0")) return url.replace("dl=0", "dl=1");
  if (!url.includes("dl=1")) {
    return url.includes("?") ? `${url}&dl=1` : `${url}?dl=1`;
  }
  return url;
};

/** Normalize CSV / external design links for browser rendering. */
export const resolveExternalImageUrl = (url) => {
  const normalized = extractHttpUrl(url);
  if (!normalized) return null;
  return convertDropboxUrl(resolveSupplierImageUrl(normalized));
};

/** Pick the first usable http(s) URL from a batch/order image record. */
export const getImageSourceUrl = (image) =>
  resolveExternalImageUrl(
    extractHttpUrl(image?.url, image?.printFile?.url, image?.name)
  );

/**
 * Hosts that don't send CORS headers, so the browser can't draw their images
 * onto a canvas (the canvas becomes tainted and export throws). These must be
 * fetched through the server proxy instead of loaded client-side. Dropbox and
 * riverr-mockup-studio (Firebase/GCS) are the main offenders; Google Drive
 * links are already rewritten to the CORS-friendly lh3 CDN by
 * resolveSupplierImageUrl, so they're not listed.
 */
export const isCorsRestrictedImageHost = (url) => {
  const resolved = extractHttpUrl(url);
  if (!resolved) return false;
  try {
    const { hostname } = new URL(resolved);
    return (
      hostname === "dropbox.com" ||
      hostname.endsWith(".dropbox.com") ||
      hostname === "dropboxusercontent.com" ||
      hostname.endsWith(".dropboxusercontent.com") ||
      hostname === "firebasestorage.googleapis.com" ||
      hostname === "storage.googleapis.com" ||
      hostname === "ssactivewear.com" ||
      hostname.endsWith(".ssactivewear.com") ||
      hostname === "sanmar.com" ||
      hostname.endsWith(".sanmar.com")
    );
  } catch {
    return false;
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const imageElementToDataUrl = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // Data URLs are same-origin. Setting crossOrigin on them can fail the load.
    if (!String(url).startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        if (!width || !height) {
          reject(new Error("Image has no dimensions"));
          return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = url;
  });

// @react-pdf/renderer only embeds PNG and JPEG. WebP (and anything else the
// browser fetched as-is) is dropped, which leaves the mockup frame blank.
const PDF_SAFE_DATA_URL = /^data:image\/(?:png|jpe?g);base64,/i;

const toPdfSafeDataUrl = async (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (PDF_SAFE_DATA_URL.test(dataUrl)) return dataUrl;
  try {
    return await imageElementToDataUrl(dataUrl);
  } catch (_) {
    return null;
  }
};

/**
 * Download an external image and return a data URL safe for fabric/PDF export.
 * Tries browser fetch/Image first (many GCS objects send ACAO: * — e.g. candle
 * jar blanks), then the server proxy for hosts that still block canvas.
 * The result is always PNG or JPEG. Other formats are redrawn to PNG.
 */
export const loadImageAsDataUrl = async (url) => {
  const resolved = resolveExternalImageUrl(url);
  if (!resolved) return null;

  let dataUrl = null;

  try {
    const response = await fetch(resolved, {
      mode: "cors",
      credentials: "omit",
    });
    if (response.ok) {
      const blob = await response.blob();
      if (blob.size > 0) {
        dataUrl = await blobToDataUrl(blob);
      }
    }
  } catch (_) {
    // Fall through to Image element / proxy.
  }

  if (!dataUrl) {
    try {
      dataUrl = await imageElementToDataUrl(resolved);
    } catch (_) {
      /* proxy below */
    }
  }

  if (!dataUrl && isCorsRestrictedImageHost(resolved)) {
    try {
      const { proxyExternalImage } = await import("../api/batches");
      const response = await proxyExternalImage(resolved);
      dataUrl = response?.data?.dataUrl || null;
    } catch (_) {
      dataUrl = null;
    }
  }

  return toPdfSafeDataUrl(dataUrl);
};

export const fileNameExtractor = (file) => (file?.name || "").split(".")?.[0];

const PIXEL_TO_INCH_CONST = 96;

export const pixelToInch = (px, maxPix, sizeIn) => (sizeIn / maxPix) * px;
export const inchToPixel1 = (inch, maxIn, sizePix) => (sizePix / maxIn) * inch;

export const inchToPixel = (inch) => inch * PIXEL_TO_INCH_CONST;

export const pxToInch = (px) => px / PIXEL_TO_INCH_CONST;

function padZero(str, len) {
  len = len || 2;
  var zeros = new Array(len).join("0");
  return (zeros + str).slice(-len);
}

export function invertColor(hex = "#ffffff") {
  if (!hex) {
    return "";
  }
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    return "";
  }
  // invert color components
  var r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16),
    g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16),
    b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
  // pad each with zeros and return
  return "#" + padZero(r) + padZero(g) + padZero(b);
}

export function diffHours(dt2, dt1) {
  var diff = (dt2.getTime() - dt1.getTime()) / 1000;
  diff /= 60 * 60;
  return Math.abs(Math.round(diff));
}

export function downloadXmlFile(xmlString, fileName) {
  const blob = new Blob([xmlString], { type: "text/xml" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const getFirstIncompleteStep = (stepsCompleted, steps) => {
  const stepsKeys = steps.map((f) => f.key);

  if (isEmpty(stepsCompleted)) {
    return stepsKeys[0];
  }

  const result = find(
    stepsKeys,
    (element) => !stepsCompleted.includes(element)
  );

  return result;
};
